<template>
  <Modal v-if="cancelMail.reserve.id" size="big" title="予約キャンセル" :staticFooter="isSP" @close="close">
    <template #content>
      <div class="contents">
        <div class="reserve-cancellation">
          <p class="head-text">
            予約をキャンセルする場合、お客様へのメッセージ送信が必須となります。メッセージの記載をお願いします。
          </p>
          <dl v-if="isExceed" class="cancel-reason">
            <dt>
              <b>キャンセル種別</b>
              <RequiredLabel />
            </dt>
            <dd>
              <SelectBox v-model.number="status" :items="cancelStatus" class="sp-reason">
                <option value="" disabled selected>理由を選択してください</option>
              </SelectBox>
              <PopoverSelectBox
                class="reason"
                v-model.number="status"
                :items="cancelStatus"
                placeholder="理由を選択してください"
              />
            </dd>
          </dl>
          <dl class="cancel-reason">
            <dt>
              <b>キャンセル理由</b>
              <RequiredLabel v-if="reason === 99" text="追加メッセージ必須" />
              <RequiredLabel v-else />
            </dt>
            <dd v-if="alert" class="alert">
              <p>確定後の予約キャンセルは、クレームの原因となります。</p>
              <p>{{ alert }}</p>
              <p>※ユーザークレームに発展した場合には、ネット予約一時停止の対象となる場合がございます。</p>
            </dd>
            <dd>
              <SelectBox v-model.number="reason" :items="reasons" class="sp-reason">
                <option value="" disabled selected>理由を選択してください</option>
              </SelectBox>
              <PopoverSelectBox
                class="reason"
                v-model.number="reason"
                :items="reasons"
                placeholder="理由を選択してください"
              >
                <template #beforeItem="{ index }">
                  <div v-if="index === 0" class="label">お客様の都合：</div>
                  <template v-else-if="index === (isExceed ? 3 : 2)">
                    <hr />
                    <div class="label">お店の都合：</div>
                  </template>
                  <template v-else-if="index === (isExceed ? 6 : 5)">
                    <hr />
                    <div class="label">その他：</div>
                  </template>
                </template>
              </PopoverSelectBox>
            </dd>
          </dl>
          <p class="desc">
            ※キャンセル理由はメール本文でお客様へもお伝えするため、間違いのないようにお願いします。
            <br />※キャンセル理由の詳細については、下部メッセージ項目において一言ご記載ください。
          </p>
        </div>
        <div class="mail-area">
          <dl>
            <dt>
              <p class="heading">件名</p>
            </dt>
            <dd v-if="isSettled">[ぐるなび] ご予約がキャンセルされました</dd>
            <dd v-else>[ぐるなび] リクエストされたご予約が不成立となりました</dd>
          </dl>
          <hr />
          <dl>
            <dt>
              <p class="heading">メッセージ<span>(追加入力 1000文字まで)</span></p>
            </dt>
            <dd>
              <label class="message-area">
                <div class="auto-message">
                  <p>{{ message }}</p>
                  <p>[予約番号]{{ reserve.syncRsvId }}</p>
                  <p>キャンセル事由：{{ reasonMessage }}</p>
                </div>
                <TextArea v-model="body" placeholder="ここにメッセージを入力してください。" />
              </label>
            </dd>
          </dl>
          <template v-if="isSP">
            <CheckBox class="bcc" v-model="isSendNote">ご登録メールアドレスに控えを送る</CheckBox>
            <dl class="sp-sendmail">
              <Popover
                class="stafflist"
                placement="top-start"
                :open="isShowStaffList"
                :disabled="!isShowStaffList"
                @auto-hide="isShowStaffList = false"
              >
                <StaffSelect slot="popover" :staff-list="staffList" @select="selectStaff" />
                <Button primary :disabled="invalid || isShowStaffList" @click="onClickConfirm">確定</Button>
              </Popover>
            </dl>
          </template>
        </div>
      </div>
    </template>
    <template v-if="!isSP" #footer-center>
      <div class="buttons">
        <CheckBox class="bcc" small v-model="isSendNote">ご登録メールアドレスに控えを送る</CheckBox>
        <Button class="sp-hide" @click="close">戻る</Button>
        <Popover
          class="stafflist"
          placement="top-start"
          :open="isShowStaffList"
          :disabled="!isShowStaffList"
          @auto-hide="isShowStaffList = false"
        >
          <StaffSelect slot="popover" :staff-list="staffList" @select="selectStaff" />
          <Button primary :disabled="invalid || isShowStaffList" @click="onClickConfirm">確定</Button>
        </Popover>
      </div>
    </template>
  </Modal>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { models } from '@/utils/vue'
import { padZero } from '@/utils/number'
import { formatDate, toHourClock } from '@/utils/date'
import TextArea from '@/components/atoms/TextArea'
import Popover from '@/components/molecules/Popover'
import PopoverSelectBox from '@/components/molecules/PopoverSelectBox'
import StaffSelect from '@/components/molecules/StaffSelect'
import Dialog from '@/components/molecules/ModalDialog'
import Modal from '@/components/molecules/Modal'
import Button from '@/components/atoms/Button'
import CheckBox from '@/components/atoms/CheckBox'
import SelectBox from '@/components/atoms/SelectBox'
import RequiredLabel from '@/components/molecules/RequiredLabel'

export default {
  name: 'CancelMailModal',
  components: {
    TextArea,
    Popover,
    PopoverSelectBox,
    StaffSelect,
    Modal,
    Button,
    CheckBox,
    SelectBox,
    RequiredLabel,
  },
  data() {
    return {
      isShowStaffList: false,
    }
  },
  computed: {
    ...mapState({
      isSP: state => state.app.isSP,
      reserve: state => state.reserves.detail.reservation,
      cancelMail: state => state.reserves.cancelMail,
    }),
    ...mapGetters({
      staffList: 'staffs/all',
    }),
    ...models(
      ['reason', 'body', 'isSendNote', 'status'],
      ({ cancelMail }, key) => cancelMail.message[key],
      ({ $store }, key, value) => $store.commit('reserves/cancelMail/update', { [key]: value }),
    ),
    isSettled() {
      return this.reserve.status !== 2
    },
    alert() {
      if (!this.isSettled) return null
      if ([42, 43].includes(this.reason)) {
        return 'お客様への複数回のご連絡、また留守電やメッセージ機能でお店からのご連絡である旨をお伝え済みでしょうか。'
      }
      if ([31, 32, 33].includes(this.reason)) {
        return 'やむを得ずキャンセルする場合は、お客さまにご説明、ご納得いただいたうえでキャンセルの対応をお願いします。'
      }
      return null
    },
    message() {
      const { date, hour, minute } = this.reserveDatetime
      return `ぐるなびネット予約をご利用いただき、ありがとうございます。${formatDate(
        date,
        'YYYY/M/D (dd)',
      )} ${hour}時${padZero(minute)}分 のご予約${
        this.isSettled ? 'がキャンセルされました。\nキャンセルされたご' : 'を承れませんでした。\n不成立となった'
      }予約は以下の通りです。`
    },
    cancelStatus() {
      return new Map([
        [1, 'キャンセル'],
        [0, '無断キャンセル'],
      ])
    },
    reasons() {
      const reasonMap = new Map([
        [41, 'お客様からキャンセルのご連絡があった'],
        [42, '必要な確認事項が発生したが、お客様と連絡が取れない'],
        [43, '来店時間になったが、お客様が来店されなかった'],
        [31, '席をご用意できない'],
        [32, '来店日が休店日もしくは貸切'],
        [33, '当店の都合によるキャンセルを電話・メッセージで合意した'],
        [99, 'その他（メッセージを入力してください）'],
      ])
      if (!this.isExceed) reasonMap.delete(43)
      return reasonMap
    },
    reasonMessage() {
      return (
        {
          31: 'お席をご用意できませんでした',
          32: 'ご来店日は休店日もしくは貸切となっています',
          33: '【連絡済】　当店の都合によりお客さまにキャンセルのご連絡をさせていただきました',
          41: '【連絡済】　お客様のご要望の通りキャンセルさせていただきました',
          42: '必要な確認事項を連絡させていただきましたが、お客様と連絡が取れませんでした',
          43: 'ご予約された日時にご来店されず、連絡も取れなかったため、予約をキャンセルさせていただきました。',
          99: 'メッセージ内容をご確認ください',
        }[this.reason] || '理由を選択してください'
      )
    },
    reserveDatetime() {
      return toHourClock(this.reserve.reserveDatetime)
    },
    isExceed() {
      // 日時をミリ秒単位で比較。予約時間を過ぎたらtrueを返す
      return +new Date() > +this.reserve.reserveDatetime
    },
    invalid() {
      const required = this.isExceed ? !(this.status !== null && this.reason) : !this.reason
      return required || this.body.length > 1000 || (this.reason === 99 && !this.body.trim())
    },
  },
  beforeDestroy() {
    this.$store.commit('reserves/cancelMail/hide')
  },
  methods: {
    close() {
      this.isShowStaffList = false
      this.$store.commit('reserves/cancelMail/hide')
    },
    async sendCancelMail({ staff }) {
      const { dispatch } = this.$store
      if (await dispatch('reserves/cancelMail/send', { staffId: staff.id })) {
        this.$emit('sendDone', this.reserve.id)
        this.close()
      }
    },
    async onClickConfirm() {
      if (this.reserveDatetime.date < toHourClock(new Date()).date) {
        if (
          await Dialog.show({
            title: 'エラー',
            message: '来店日の翌日6時以降はキャンセルメールを送信できません。',
            okText: '閉じる',
          })
        ) {
          this.close()
          return
        }
      }

      if (this.staffList.length > 1) {
        this.isShowStaffList = true
      } else {
        this.selectStaff(this.staffList[0])
      }
    },
    selectStaff(staff) {
      this.isShowStaffList = false
      this.sendCancelMail({ staff })
    },
  },
}
</script>

<style lang="postcss" scoped>
.contents {
  display: grid;
  grid-gap: 16px;
  padding: 16px 24px;

  .responsive & {
    @media screen and (--sp-modal) {
      box-sizing: border-box;
      grid-gap: 24px;
      height: 100%;
      padding: 16px;
      width: 100%;
    }
  }

  .reserve-cancellation {
    display: grid;
    grid-gap: 8px;

    .head-text {
      font-size: 13px;
      line-height: 19px;
      padding-bottom: 8px;
    }

    dl {
      display: grid;
      grid-gap: 8px;

      & + dl {
        margin-top: 8px;
      }

      dt {
        align-items: center;
        display: grid;
        grid-auto-flow: column;
        grid-gap: 8px;
        justify-content: flex-start;
        line-height: 1;
      }

      .alert {
        color: var(--pure-red);
        font-size: 13px;
        line-height: 19px;
      }

      .reason {
        /* TODO >>> を無くしたい */
        >>> .field {
          width: 432px;
        }
      }

      .sp-reason {
        display: none;
      }
    }

    .desc {
      font-size: 11px;
      line-height: 16px;
    }

    .responsive & {
      @media screen and (--sp-modal) {
        grid-gap: 16px;

        .head-text {
          font-size: 15px;
          line-height: 21px;
          padding-bottom: 0;
        }

        .cancel-reason {
          .reason {
            display: none;
          }

          .sp-reason {
            display: block;

            >>> select {
              overflow: hidden;
            }

            option:disabled {
              display: none;
            }
          }
        }
      }
    }
  }

  .mail-area {
    background-color: var(--mostly-white);
    border-bottom: 1px solid var(--very-light-gray-2);
    border-top: 1px solid var(--very-light-gray-2);
    display: grid;
    grid-gap: 16px;
    margin: -8px -24px -24px;
    padding: 16px 24px;

    dl {
      display: grid;
      grid-gap: 8px;

      dt {
        line-height: 1;
      }
    }

    hr {
      background-color: var(--very-light-gray-2);
      border: none;
      display: none;
      height: 1px;
      margin: 0;
      width: 100%;
    }

    .heading {
      font-weight: 600;

      span {
        margin-left: 4px;
      }
    }

    .message-area {
      background-color: var(--white);
      border: 1px solid var(--border-color);
      border-radius: 4px;
      box-sizing: border-box;
      display: block;
      height: 224px;
      overflow-y: scroll;
      padding: 8px;

      textarea {
        border: 0;
        min-height: 64px;
        padding: 16px 8px;

        &:focus {
          border: 0;
        }
      }
    }

    .auto-message {
      background-color: var(--very-light-gray-4);
      font-size: 13px;
      padding: 8px;
      white-space: pre-line;
    }

    .sp-sendmail {
      display: none;

      button {
        border-radius: 4px;
        font-size: 15px;
        height: 40px;
        line-height: normal;
        width: 100%;
      }

      .stafflist {
        width: 100%;
      }
    }

    .responsive & {
      @media screen and (--sp-modal) {
        border-top: none;
        margin: 0 -16px -24px;
        padding: 24px;

        hr {
          display: initial;
        }

        .heading {
          font-size: 18px;

          span {
            font-size: 11px;
            font-weight: initial;
          }
        }

        .message-area {
          height: 330px;
          margin-top: 8px;
          padding: 16px;
          transform: translate3d(0, 0, 0);

          textarea {
            padding: 16px 0;

            textarea::placeholder {
              color: var(--light-gray);
            }
          }
        }

        .auto-message {
          line-height: 23px;
          padding: 16px;
        }

        .sp-sendmail {
          display: initial;
        }
      }
    }
  }
}

.label {
  color: var(--dark-gray);
  font-size: 13px;
  padding: 8px 16px;
}

.buttons {
  display: grid;
  grid-gap: 16px 32px;
  grid-template-columns: 1fr 1fr;
  justify-items: center;
  padding: 8px 0 16px;

  .bcc {
    grid-column: 1 / -1;
  }

  .responsive & {
    @media screen and (--sp-modal) {
      grid-template-columns: 1fr;
      padding: 8px;

      .sp-hide {
        display: none;
      }

      .stafflist {
        width: 100%;
      }
    }
  }
}

.responsive {
  @media screen and (--sp-modal) {
    .bcc {
      align-items: center;
      justify-content: center;
      width: auto;
    }
  }
}
</style>
